import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GoogleAccountLink } from "@/components/GoogleAccountLink";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { phoneSearch } from "@/lib/phone";
import { AccountProfileForm } from "@/components/AccountProfileForm";
import { EntityLink } from "@/components/EntityLink";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { DealerReferralProgram } from "@/components/DealerReferralProgram";
import { Users, Gift, Share2, Wallet as WalletIcon, Copy, Smartphone, Search, ShieldCheck, UserCog, CheckCircle2 } from "lucide-react";

const TABS = [
  { key: "account", label: "Gestione Account", title: "Gestione Account" },
  { key: "referral", label: "Invita & Guadagna", title: "Invita & Guadagna" },
  { key: "sicurezza", label: "Sicurezza", title: "Sicurezza" },
];

export default function Account({ only }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [referralOn, setReferralOn] = useState(false);
  useEffect(() => { api.get("/settings/features").then((r) => setReferralOn(!!r.data.referral_page_enabled)).catch(() => {}); }, []);
  const visible = TABS.filter((t) => t.key !== "sicurezza" && (t.key !== "referral" || referralOn));
  const tab = only || (visible.some((t) => t.key === params.get("tab")) ? params.get("tab") : "account");
  const setTab = (value) => setParams({ tab: value });
  const [plafond, setPlafond] = useState(0);
  const active = TABS.find((t) => t.key === tab);
  const roleLabel = { admin: "Amministratore", dealer: "Dealer", customer: "Cliente", agent: "Agenzia", support: "Assistenza" }[user?.role] || "Utente";

  useEffect(() => { api.get("/wallet").then((r) => setPlafond(r.data.balance)).catch(() => setPlafond(0)); }, []);

  return (
    <div data-testid="account-page">
      {!only && visible.length > 1 && <div className="flex flex-wrap gap-2 pb-5 border-b border-border">
        {visible.map((t) => (
          <button key={t.key} type="button" data-testid={`account-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-5 py-2.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors border ${
              tab === t.key ? "bg-[#0f1b2d] text-white border-transparent" : "bg-card text-muted-foreground border-border hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>}

      <div className={only ? "" : "mt-8"}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold tracking-wider uppercase text-primary min-w-0 max-w-full [overflow-wrap:anywhere]" data-testid="account-header-email">{user?.email}</span>
          <Badge className="bg-primary hover:bg-primary text-primary-foreground uppercase text-[10px] tracking-wider">{roleLabel}</Badge>
          {user?.role !== "customer" && <Badge variant="outline" className="uppercase text-[10px] tracking-wider">Azienda</Badge>}
          <Badge variant="outline" className="uppercase text-[10px] tracking-wider text-teal-500 border-teal-500/40" data-testid="account-plafond-badge">Plafond {euro(plafond)}</Badge>
        </div>
        <h1 className="font-head text-4xl sm:text-5xl font-bold tracking-tight mt-4" data-testid="account-title">{active?.title}</h1>
      </div>

      <div className="mt-8" data-testid={`account-panel-${tab}`}>
        {tab === "referral" && <Referral onBalance={setPlafond} />}
        {tab === "account" && <GestioneAccount />}
        {tab === "sicurezza" && <div className="space-y-6 max-w-2xl"><GoogleAccountLink /><Sicurezza /></div>}
      </div>
    </div>
  );
}

function Referral() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referral").then((r) => setData(r.data)).catch(() => {}); }, []);
  const link = data ? `${window.location.origin}/r/${data.code}` : "";
  const s = data?.stats || {};
  const cards = [
    { icon: Users, label: "Iscritti", value: s.iscritti ?? 0 },
    { icon: Gift, label: "Hanno acquistato", value: s.acquistato ?? 0 },
    { icon: Share2, label: "Guadagnato", value: euro(s.guadagnato) },
    { icon: WalletIcon, label: "Nel tuo plafond", value: euro(s.nel_plafond) },
    { icon: WalletIcon, label: "Da incassare", value: euro(s.da_incassare) },
    { icon: CheckCircle2, label: "Già pagato", value: euro(s.gia_pagato) },
  ];
  const share = (how) => {
    if (how === "copy") { navigator.clipboard?.writeText(link); toast.success("Link copiato"); }
    if (how === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent("Iscriviti con il mio link: " + link)}`, "_blank");
    if (how === "email") window.open(`mailto:?subject=Invito&body=${encodeURIComponent("Iscriviti: " + link)}`, "_blank");
  };
  return (
    <div className="space-y-6">
      <Card className="p-8 glow-border" style={{ background: "#0f1b2d" }} data-testid="referral-banner">
        <h2 className="font-head text-3xl font-bold text-white">{data?.dealer_program ? "Invita dealer e clienti" : "Invita amici, guadagna il 10%"}</h2>
        <p className="text-white/70 mt-3 max-w-2xl text-sm leading-relaxed" data-testid="referral-banner-text">{data?.dealer_program
          ? `Condividi il tuo link personale. Un nuovo dealer, al suo primo acquisto, fa arrivare ${euro(data.dealer_program.referrer_bonus)} di credito a te e ${euro(data.dealer_program.invitee_bonus)} a lui. Un cliente privato ha il 10% di sconto sul primo ordine e tu guadagni il 10% sui suoi acquisti.`
          : "Condividi il tuo link personale: chi si registra ha il 10% di sconto sul primo ordine e tu guadagni il 10% su ogni suo acquisto o ricarica, per sempre. Ai privati la provvigione arriva subito nel plafond; agli account Business si accumula come saldo da pagare."}</p>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <div className="flex-1 min-w-[240px] rounded-full bg-white/10 px-5 py-3 font-mono text-white text-sm truncate" data-testid="referral-link">{link}</div>
          <Button className="rounded-full gap-2 bg-primary hover:bg-primary/90" onClick={() => share("copy")} data-testid="referral-copy"><Copy className="w-4 h-4" /> Copia link</Button>
          <Button variant="secondary" className="rounded-full" onClick={() => share("whatsapp")} data-testid="referral-whatsapp">WhatsApp</Button>
          <Button variant="secondary" className="rounded-full" onClick={() => share("email")} data-testid="referral-email">Email</Button>
        </div>
      </Card>
      {data?.dealer_program && <DealerReferralProgram p={data.dealer_program} />}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-5 glow-border" data-testid={`referral-stat-${c.label}`}>
            <c.icon className="w-5 h-5 text-primary" />
            <p className="font-head text-2xl font-bold mt-3">{c.value}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{c.label}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ConsoleSim() {
  const [sims, setSims] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/sims").then((r) => setSims(r.data || [])).catch(() => {}); }, []);
  const stats = [
    { label: "Totali", value: sims.length },
    { label: "Attive", value: sims.filter((s) => (s.status || "active") === "active").length },
    { label: "Sospese", value: sims.filter((s) => s.status === "suspended").length },
    { label: "Esaurite", value: sims.filter((s) => s.status === "depleted").length },
    { label: "Sotto il 20%", value: 0 },
  ];
  const filtered = sims.filter((s) => !q || (s.iccid || "").includes(q) || (s.phone_number || "").includes(q) || (phoneSearch(q).length > 2 && phoneSearch(s.phone_number).includes(phoneSearch(q))));
  return (
    <div className="space-y-6">
      <p className="text-muted-foreground text-sm -mt-2">La tua flotta a colpo d'occhio: stato, giga, date e azioni per ogni SIM.</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-5 glow-border" data-testid={`console-stat-${s.label}`}>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</p>
            <p className="font-head text-3xl font-bold mt-2">{s.value}</p>
          </Card>
        ))}
      </div>
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Cerca ICCID o numero…" data-testid="console-search" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <Card className="glow-border">
        <Table>
          <TableHeader><TableRow><TableHead>ICCID</TableHead><TableHead>Numero</TableHead><TableHead>Operatore</TableHead><TableHead>Offerta</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.id} data-testid={`console-sim-${s.id}`}>
                <TableCell className="font-mono text-xs"><EntityLink kind="sim" id={s.id} data-testid={`account-sim-link-${s.id}`}>{s.iccid || "—"}</EntityLink></TableCell>
                <TableCell><PhoneDisplay value={s.phone_number} data-testid={`account-sim-phone-${s.id}`} /></TableCell>
                <TableCell>{s.operator || "—"}</TableCell>
                <TableCell>{s.plan_name || "—"}</TableCell>
                <TableCell><Badge variant="outline">{s.status || "active"}</Badge></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessuna SIM.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function SimList() {
  const [sims, setSims] = useState([]);
  useEffect(() => { api.get("/sims").then((r) => setSims(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="esim-list">
      {sims.map((s) => (
        <Card key={s.id} className="p-5 glow-border" data-testid={`esim-card-${s.id}`}>
          <div className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-primary" /><Badge variant="outline">{s.operator || "—"}</Badge></div>
          <h3 className="font-head font-semibold mt-2"><EntityLink kind="sim" id={s.id} data-testid={`account-sim-card-link-${s.id}`}><PhoneDisplay value={s.phone_number} fallback="eSIM" data-testid={`account-phone-card-${s.id}`} /></EntityLink></h3>
          <p className="text-sm text-muted-foreground mt-1">{s.plan_name || "—"} · <EntityLink kind="customer" id={s.customer_id} data-testid={`account-sim-customer-link-${s.id}`}>{s.customer_name || "—"}</EntityLink></p>
        </Card>
      ))}
      {sims.length === 0 && <p className="text-muted-foreground">Nessuna eSIM attiva.</p>}
    </div>
  );
}

function GestioneAccount() {
  return <Card className="p-4 sm:p-6 max-w-3xl" data-testid="account-profile-card">
    <div className="flex items-center gap-2 mb-4"><UserCog className="w-5 h-5 text-primary" /><h2 className="font-head text-lg font-semibold" data-testid="account-profile-heading">La mia anagrafica</h2></div>
    <AccountProfileForm />
  </Card>;
}

function Sicurezza() {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [loading, setLoading] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/account/password", form); toast.success("Password aggiornata"); setForm({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore"); }
    finally { setLoading(false); }
  };
  return (
    <Card className="p-6 glow-border max-w-xl" data-testid="security-card">
      <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-primary" /><h2 className="font-head text-lg font-semibold">Cambia password</h2></div>
      <form onSubmit={save} className="space-y-4">
        <div><Label>Password attuale</Label><Input type="password" className="mt-1.5" data-testid="security-current" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required /></div>
        <div><Label>Nuova password</Label><Input type="password" className="mt-1.5" data-testid="security-new" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required minLength={6} /></div>
        <Button type="submit" disabled={loading} data-testid="security-save">{loading ? "Salvataggio…" : "Aggiorna password"}</Button>
      </form>
    </Card>
  );
}
